/**
 * The backend's AI reading of an alert (raw_payload.alert_analysis).
 * The panel renders this as-is; nothing here re-derives the alert type.
 */
import type { AmsIncident } from '../services/api';
import { rcaByType } from './engineeringArtifacts';

export type CodeChange = 'required' | 'not_required' | 'uncertain';

export interface AlertAnalysis {
  category: string;
  summary?: string;
  what_happened: string;
  root_cause: string;
  confidence: 'high' | 'medium' | 'low';
  evidence: string[];
  expected?: string;
  observed?: string;
  affected_component?: string;
  impact: string[];
  code_change: CodeChange;
  code_change_reason: string;
  fix_steps: string[];
  verification_steps: string[];
  alert_details: Array<{ label: string; value: string }>;
  source: 'ai' | 'rules';
  generated_at?: string;
}

const list = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [];

export function extractAlertAnalysis(incident?: AmsIncident | null): AlertAnalysis | null {
  // Stored in raw_payload (merged atomically); older builds kept it in rca.
  const raw = (incident?.raw_payload?.alert_analysis ?? rcaByType(incident, 'alert_analysis')?.analysis) as
    | Record<string, unknown>
    | undefined;
  if (!raw || typeof raw !== 'object' || !raw.what_happened) return null;
  const codeChange = raw.code_change;
  return {
    category: String(raw.category ?? ''),
    summary: raw.summary ? String(raw.summary) : undefined,
    what_happened: String(raw.what_happened ?? ''),
    root_cause: String(raw.root_cause ?? ''),
    confidence: raw.confidence === 'high' || raw.confidence === 'medium' ? raw.confidence : 'low',
    evidence: list(raw.evidence),
    expected: raw.expected ? String(raw.expected) : undefined,
    observed: raw.observed ? String(raw.observed) : undefined,
    affected_component: raw.affected_component ? String(raw.affected_component) : undefined,
    impact: list(raw.impact),
    code_change: codeChange === 'required' || codeChange === 'not_required' ? codeChange : 'uncertain',
    code_change_reason: String(raw.code_change_reason ?? ''),
    fix_steps: list(raw.fix_steps),
    verification_steps: list(raw.verification_steps),
    alert_details: Array.isArray(raw.alert_details)
      ? (raw.alert_details as Array<Record<string, unknown>>)
          .filter((d) => d && d.label && d.value)
          .map((d) => ({ label: String(d.label), value: String(d.value) }))
      : [],
    source: raw.source === 'ai' ? 'ai' : 'rules',
    generated_at: raw.generated_at ? String(raw.generated_at) : undefined,
  };
}

/** Plain-language fix path shown next to the problem type. */
export const FIX_PATH_LABEL: Record<CodeChange, string> = {
  required: 'Code change required',
  not_required: 'Operational fix — no code change',
  uncertain: 'Needs judgement',
};
